// @flow
import * as React from 'react';
import Select, {
    components, ClearIndicatorProps, DropdownIndicatorProps,
} from 'react-select';
import type { InputActionMeta, ValueType } from 'react-select/src/types';
import {
    memoize, debounce, isEqual, get, isEmpty,
} from 'lodash';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import CreatableSelect from 'react-select/creatable';
import memoizeOne from 'memoize-one';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import StyledCloseButton from '../grommet2/forms/fields/StyledCloseButton';
import StyledArrowOpenButton from '../grommet2/forms/fields/StyledArrowOpenButton';
import StyledArrowCloseButton from '../grommet2/forms/fields/StyledArrowCloseButton';


type Props = {
    choices: {
        [any]: string,
    },
    onChange: (value: ValueType) => void,
    onBlur: (e: SyntheticFocusEvent<any>) => void,
    initialObjectId: any,
    value: any;
    name: string;
    multi: boolean,
    emptyLabel: string,
    // eslint-disable-next-line
    resourceName: string,
    // eslint-disable-next-line
    resourceMethod: string,
    // eslint-disable-next-line
    resourceArguments?: string[],
    // eslint-disable-next-line
    initialOptionResourceMethod: string,
    // eslint-disable-next-line
    searchField: string,
    // eslint-disable-next-line
    additionalFilters: Object,
    // eslint-disable-next-line
    valueField: string,
    valueTypePK: boolean,
    valueFields: Array,
    // eslint-disable-next-line
    labelFields: Array<string>,
    formatLabel: (option: Object, props: Props) => string,
    minSearchLength: number,
    preload: boolean,
    cacheOptions: boolean,
    styles: Object,
    setFirstVal: Function,
    additionalValueFilters: Object,
    initialSearchText: string,
    menuPortalTarget?: Node,
    menuPlacement?: string,
    closeMenuOnSelect?: boolean,
    creatable?: boolean,
    intl: IntlShape,
    noOptionsMessage?: () => React.Node,
    disabled?: boolean,
    allowEmptyChoice?: boolean,
    onCreateOption?: (inputValue: string) => void,
    formatOptionLabel?: (data: Object, meta: Object) => React.Node,
}

type State = {
    loading: boolean,
    options: Array<{ label: string, value: any }>,
    selectedValue: Array<{ label: string, value: any }> | null | { label: string, value: any },
}

type CombinedProps = Props & {
    getData: (searchText: string) => Promise<Object>,
    getInitialOption: (value: string) => Promise<Object>,
    getInitialOptionMany: (query: Object) => Promise<Object>,
}

const ClearIndicator = (props: ClearIndicatorProps) => {
    const { selectProps: { value } } = props;
    return (
        <components.ClearIndicator {...props}>
            { value[0] || value.value ? <StyledCloseButton /> : ' ' }
        </components.ClearIndicator>
    );
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
    const { selectProps: { menuIsOpen } } = props;
    return (
        <components.DropdownIndicator {...props}>
            {menuIsOpen ? <StyledArrowCloseButton plain /> : <StyledArrowOpenButton plain /> }
        </components.DropdownIndicator>
    );
};

const defaultFormatLabel = (option: Object, props: Props) => {
    let label = '';
    if (props.labelFields) {
        props.labelFields.forEach((lf) => {
            if (get(option, lf)) label = `${label} ${get(option, lf)}`;
        });
    }

    return label;
};

const createManualOption = (value: string, label: string) => ({
    value,
    label,
    _original: {},
});

const messages = defineMessages({
    creatablePrefix: {
        id: 'creatable-prefix',
        defaultMessage: 'Create',
    },
});

const filterOptions = () => true;

@connectAPI((apiClient: ApiClient, props: Props) => ({
    getData: {
        send: (searchText: Object) => get(apiClient.resources, props.resourceName)[props.resourceMethod](
            ...props.resourceArguments,
            {
                [props.searchField]: searchText,
                ...props.additionalFilters,
            },
        ),
    },
    getInitialOption: {
        send: (value: string) => get(apiClient.resources, props.resourceName)[props.initialOptionResourceMethod](value),
    },
    getInitialOptionMany: {
        send: (query: Object) => get(apiClient.resources, props.resourceName).list(query),
    },
}), true)
class AsyncSelect extends React.Component<CombinedProps, State> {
    select: { current: ?typeof Select };

    cachedOptions = {};

    loadOptionsCounter: number = 0;

    constructor(props: CombinedProps) {
        super(props);

        const { multi, emptyLabel, allowEmptyChoice } = props;

        this.state = {
            options: !multi && allowEmptyChoice ? [{
                label: emptyLabel,
                value: null,
                _original: {},
            }] : [],
            loading: false,
            selectedValue: null,
        };

        this.getValue = memoize(this.getValue);
        this.loadOptions = debounce(this.loadOptions, 200);

        this.select = React.createRef();
    }

    componentDidMount() {
        const {
            value, initialObjectId, valueTypePK,
        } = this.props;

        if (valueTypePK && (initialObjectId || value)) {
            this.loadInitialOption();
        } else {
            this.loadOptions('');
        }
    }

    componentDidUpdate(prevProps: CombinedProps) {
        const {
            resourceName, resourceMethod, additionalFilters, preload, value, initialSearchText, multi, choices,
        } = this.props;
        const { value: prevValue } = prevProps;

        if (
            resourceName !== prevProps.resourceName
            || resourceMethod !== prevProps.resourceMethod
            || !isEqual(additionalFilters, prevProps.additionalFilters)
            || initialSearchText !== prevProps.initialSearchText
            || value !== prevValue
        ) {
            if (preload) {
                this.clearOptions();
                this.clearCache();
                this.loadOptions('');
            }
        }

        if (choices !== prevProps.choices) {
            if (choices) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState((prevState) => {
                    const options = [...prevState.options];

                    Object.keys(choices).forEach((key) => {
                        if (!options.find((opt) => (
                            `${opt.value}` === `${key}`
                        ))) {
                            options.push({
                                label: choices[key],
                                value: key,
                            });
                        }
                    });

                    return { options };
                });
            }
        }

        // Force update the select when value is changed because when multi select is used
        // and input field size grows the menu drop is not repositioned if rendered in a portal
        if (value !== prevValue) {
            if (this.select?.current?.select) {
                this.select.current.select.forceUpdate();
            }

            // Update the internal selected value, otherwise an invalid old option may be left
            if (multi) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState((prevState) => ({
                    selectedValue: prevState.options.filter((opt) => (
                        value && value.includes(opt.value)
                    )),
                }));
            } else {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState((prevState) => {
                    let selectedValue = this.getValue(value, prevState.options);
                    if (!selectedValue && value) {
                        selectedValue = { value, label: value };
                    }

                    return { selectedValue };
                });
            }
        }
    }

    componentWillUnmount() {
        this.clearCache();
    }

    getValue = (value: any, options: Array<{ label: string, value: any }>): any => {
        const {
            setFirstVal, additionalValueFilters, multi,
        } = this.props;

        if (multi) {
            if (value && Array.isArray(value)) {
                const result = options.filter((opt) => value.indexOf(opt.value) > -1);
                return result.length ? result : null;
            }
            return null;
        }

        const val = value ? value.toString() : value;
        let valueResults = options.filter((v) => (v.value != null));

        Object.keys(additionalValueFilters).forEach((key) => {
            valueResults = valueResults.filter((v) => v._original[key] === additionalValueFilters[key]);
        });

        if (setFirstVal && valueResults.length === 1) {
            setFirstVal(valueResults[0].value);
            return valueResults[0];
        }

        return options.find((opt) => opt.value?.toString() === val) || null;
    };

    getOptionValue = (option: { label: string, value: any }) => option.value || '';

    handleChange = (value?: Object) => {
        const { onChange, multi } = this.props;

        this.setState({
            selectedValue: value,
        });

        if (multi) {
            onChange(value ? value.map((v) => v.value) : null, value ? value.map((v) => v._original) : null);
        } else {
            onChange(value ? value.value : null, value ? value._original : null);
        }
    };

    handleInputChange = (inputValue: string, actionMeta: InputActionMeta) => {
        const { value } = this.props;
        if (actionMeta.action === 'input-change' || (
            actionMeta.action === 'input-blur' && !value
        )) {
            this.loadOptions(inputValue, true);
        }
    };

    setInitialOptions = (response: Object) => {
        const {
            formatLabel, valueField, multi, emptyLabel, valueFields, value, choices, allowEmptyChoice,
        } = this.props;
        if (response) {
            const options = !multi && allowEmptyChoice ? [{
                label: emptyLabel,
                value: null,
                _original: {},
            }] : [];

            const getLabel = formatLabel || defaultFormatLabel;

            if (!multi && response[valueField]) {
                // Single initial value
                const arr = [];
                if (valueFields) valueFields.forEach((v) => (response[v] && arr.push(response[v].toString())));

                options.push({
                    label: getLabel(response, this.props),
                    value: arr.length > 0 ? arr.join('-') : response[valueField],
                    _original: response,
                });
            } else if (response.length > 0) {
                // Many initial values
                Object.values(response).forEach((responseValue) => {
                    const arr = [];
                    if (valueFields) {
                        valueFields.forEach((v) => (responseValue[v] && arr.push(responseValue[v].toString())));
                    }

                    options.push({
                        label: getLabel(responseValue, this.props),
                        value: arr.length > 0 ? arr.join('-') : responseValue[valueField],
                        _original: responseValue,
                    });
                });
            }

            let selectedValue;

            // Update the internal selected value, otherwise an invalid old option may be left
            if (multi) {
                // eslint-disable-next-line react/no-did-update-set-state
                selectedValue = options.filter((opt) => (
                    value && value.includes(opt.value)
                ));
            } else {
                selectedValue = this.getValue(value, options);
                if (!selectedValue && value) {
                    selectedValue = { value, label: value };
                }
            }

            if (choices) {
                Object.keys(choices).forEach((key) => {
                    if (!options.find((opt) => (
                        opt.value !== null && `${opt.value}` === `${key}`
                    ))) {
                        options.push({
                            label: choices[key],
                            value: key,
                        });
                    }
                });
            }

            this.getValue.cache.clear();
            this.setState({
                options,
                loading: false,
                selectedValue,
            });
        }
    };

    loadInitialOption = () => {
        const {
            getInitialOption, getInitialOptionMany, multi, initialObjectId: objectId, value, valueField,
        } = this.props;

        this.setState({ loading: true });

        if (!multi) {
            // Single initial value
            if (objectId) {
                getInitialOption(objectId).then((response: Object) => this.setInitialOptions(response));
            } else {
                getInitialOption(value).then((response: Object) => this.setInitialOptions(response));
            }
        } else if (objectId) {
            getInitialOptionMany({ [valueField]: objectId })
                .then((response: Object) => this.setInitialOptions(response.results));
        } else {
            getInitialOptionMany({ [valueField]: value })
                .then((response: Object) => this.setInitialOptions(response.results));
        }
    };

    setOptions = (response: Object, searchText: string) => {
        const {
            formatLabel, valueField, multi, emptyLabel, valueFields,
            cacheOptions, allowEmptyChoice, choices, value,
        } = this.props;
        const options = !multi && allowEmptyChoice ? [{
            label: emptyLabel,
            value: null,
            _original: {},
        }] : [];

        if (response.results) {
            const getLabel = formatLabel || defaultFormatLabel;

            response.results.forEach((res) => {
                const arr = [];
                if (valueFields) valueFields.forEach((v) => (res[v] && arr.push(res[v].toString())));
                options.push({
                    label: getLabel(res, this.props),
                    value: arr.length > 0 ? arr.join('-') : res[valueField],
                    _original: res,
                });
            });
        }

        if (choices) {
            Object.keys(choices).forEach((key) => {
                if (!options.find((opt) => (
                    opt.value !== null && `${opt.value}` === `${key}`
                ))) {
                    options.push({
                        label: choices[key],
                        value: key,
                    });
                }
            });
        }

        const oldOptionsValue = this.getValue(value, this.state.options);

        this.getValue.cache.clear();

        // Append the selected value, to prevent deselecting it or showing it as an ID
        if (oldOptionsValue && multi) {
            oldOptionsValue.forEach((oldOpt) => {
                if (!options.find((opt) => (oldOpt.value === opt.value))) {
                    options.push(oldOpt);
                }
            });
        } else if (oldOptionsValue) {
            if (!options.find((opt) => (oldOptionsValue.value === opt.value))) {
                options.push(oldOptionsValue);
            }
        }

        if (cacheOptions) {
            this.cachedOptions[searchText] = options;
        }
        this.setState({
            options,
            loading: false,
        });
    };

    loadOptions = (searchText: string, fromInputChange = false) => {
        const {
            getData, minSearchLength, cacheOptions, initialSearchText,
        } = this.props;
        if (searchText.length >= minSearchLength) {
            if (cacheOptions && typeof this.cachedOptions[searchText] !== 'undefined') {
                this.setState({
                    options: this.cachedOptions[searchText],
                });
            }

            this.setState({ loading: true });

            this.loadOptionsCounter += 1;
            const currentLoadNumber = this.loadOptionsCounter;

            if (initialSearchText && !fromInputChange) {
                getData(initialSearchText).then((response: Object) => {
                    // Prevents race conditions where the previous request takes longer to complete and when
                    // it's done replaces the latest and correct options
                    if (currentLoadNumber === this.loadOptionsCounter) {
                        this.setOptions(response, initialSearchText);
                    }
                });
            } else {
                getData(searchText).then((response: Object) => {
                    // Prevents race conditions where the previous request takes longer to complete and when
                    // it's done replaces the latest and correct options
                    if (currentLoadNumber === this.loadOptionsCounter) {
                        this.setOptions(response, searchText);
                    }
                });
            }
        } else {
            this.clearOptions();
            this.clearCache();
        }
    };

    appendselectedValue = (options: Object): Array<{ label: string, value: any }> => {
        const { multi } = this.props;
        const { selectedValue } = this.state;

        if (!isEmpty(selectedValue)) {
            // Append manually created options
            if (options) {
                if (multi) {
                    selectedValue.forEach((value) => {
                        if (isEmpty(options.filter((option) => (option.value === value.value)))) {
                            options.push(createManualOption(value.value, value.label));
                        }
                    });
                } else if (selectedValue.value
                    && isEmpty(options.filter((option) => (option.value === selectedValue.value)))
                ) {
                    options.push(createManualOption(selectedValue.value, selectedValue.label));
                }
            } else if (multi) {
                selectedValue.forEach((value) => options.push(createManualOption(value.value, value.label)));
            } else if (selectedValue.value) {
                options.push(createManualOption(selectedValue.value, selectedValue.label));
            }
        }

        return options;
    };

    formatCreateLabel = (inputValue: string) => {
        const { intl: { formatMessage } } = this.props;
        return `${formatMessage(messages.creatablePrefix)} "${inputValue}"`;
    };

    closeMenuOnScroll = (e) => {
        if (e.target.className && e.target.className.includes('select__menu-list')) {
            return false;
        }
        // There cases where a scroll event is emitted upon opening the menu, in which case the target is the document
        // and the menu is closed immediately
        if (e.target === document) {
            return false;
        }
        return true;
    };

    clearCache() {
        this.cachedOptions = {};
    }

    clearOptions() {
        this.setState({ options: [] });
    }

    render() {
        const {
            name, onBlur, value, multi, styles, menuPortalTarget, menuPlacement, closeMenuOnSelect,
            creatable, noOptionsMessage, disabled, onCreateOption, formatOptionLabel,
        } = this.props;

        const { loading, options } = this.state;

        const optionsResult = this.appendselectedValue(options);

        return creatable ? (
            <CreatableSelect
                placeholder=""
                options={optionsResult}
                onChange={this.handleChange}
                value={this.getValue(value, optionsResult)}
                name={name}
                id={`id-${name}`}
                isMulti={multi}
                onBlur={onBlur}
                closeMenuOnSelect={typeof closeMenuOnSelect !== 'undefined' ? closeMenuOnSelect : !multi}
                isClearable
                getOptionValue={this.getOptionValue}
                onInputChange={this.handleInputChange}
                isLoading={loading}
                ref={this.select}
                styles={styles}
                menuPortalTarget={menuPortalTarget}
                menuPlacement={menuPlacement}
                formatCreateLabel={this.formatCreateLabel}
                noOptionsMessage={noOptionsMessage}
                isDisabled={disabled}
                components={{ ClearIndicator, DropdownIndicator }}
                // Disable the additional filtration since the API has already returned the relevant options, and it may
                // use a different filtration than the simpler one of react-select
                filterOption={filterOptions}
                classNamePrefix="select"
                onCreateOption={onCreateOption ? (inputValue: string) => {
                    // Forces the dropdown to close when multi is true
                    if (this.select.current) {
                        this.select.current.blur();
                    }
                    onCreateOption(inputValue);
                } : undefined}
                formatOptionLabel={formatOptionLabel}
            />
        ) : (
            <Select
                placeholder=""
                options={optionsResult}
                onChange={this.handleChange}
                value={this.getValue(value, optionsResult)}
                name={name}
                id={`id-${name}`}
                isMulti={multi}
                onBlur={onBlur}
                closeMenuOnSelect={typeof closeMenuOnSelect !== 'undefined' ? closeMenuOnSelect : !multi}
                isClearable
                getOptionValue={this.getOptionValue}
                onInputChange={this.handleInputChange}
                isLoading={loading}
                ref={this.select}
                styles={styles}
                menuPortalTarget={menuPortalTarget}
                menuPlacement={menuPlacement}
                noOptionsMessage={noOptionsMessage}
                isDisabled={disabled}
                components={{ ClearIndicator, DropdownIndicator }}
                closeMenuOnScroll={this.closeMenuOnScroll}
                // Disable the additional filtration since the API has already returned the relevant options, and it may
                // use a different filtration than the simpler one of react-select
                filterOption={filterOptions}
                classNamePrefix="select"
                formatOptionLabel={formatOptionLabel}
            />
        );
    }
}

AsyncSelect.defaultProps = {
    allowEmptyChoice: false,
    searchField: 'search',
    resourceMethod: 'list',
    initialOptionResourceMethod: 'get',
    valueField: 'id',
    valueTypePK: true,
    labelFields: [],
    formatLabel: null,
    minSearchLength: 3,
    additionalValueFilters: {},
    additionalFilters: {},
    preload: false,
    cacheOptions: true,
    menuPortalTarget: undefined,
    menuPlacement: undefined,
    closeMenuOnSelect: undefined,
    resourceArguments: [],
    creatable: false,
    intl: {},
    noOptionsMessage: undefined,
    disabled: false,
    onCreateOption: undefined,
    formatOptionLabel: undefined,
};

export default injectIntl(AsyncSelect, { forwardRef: true });
